import CheckIcon from '@mui/icons-material/Check'
import { Autocomplete, Box, ListItem, Typography } from '@mui/material'
import React, { useState, useEffect, Dispatch, SetStateAction } from 'react'

import { ITaxDetailsPayload, ITaxType, useTierSelectionContext } from '../../hooks/useTierSelection'
import sprite from '../../icons/country-flags/sprite/sprite.svg'
import { AutoCompleteStyle } from '../AutocompleteStyles'
import { CustomInput, InputField, ValidationType } from '../InputField'

interface IProps {
  taxDetails: ITaxDetailsPayload | undefined
  handleTaxDetailsChange?: (type: string, value: string) => void
  setInvalidTaxDetails?: Dispatch<SetStateAction<boolean>>
  isLoading: boolean
}

const FLAG_SIZES = {
  width: 16,
  height: 16,
}

function TaxForm(props: IProps) {
  const { taxDetails, handleTaxDetailsChange, setInvalidTaxDetails, isLoading } = props

  const { fetchTaxTypes, customerProfile } = useTierSelectionContext()

  const [taxTypes, setTaxTypes] = useState<ITaxType[]>([])
  const [taxCountryType, setTaxCountryType] = useState<string>('')
  const [taxValuePlaceholder, setTaxValuePlaceholder] = useState<string>('')
  const [taxValueValidation, setTaxValueValidation] = useState<string>('')

  const taxValueRegex = taxValueValidation ? new RegExp(`^${taxValueValidation.replace(/['"]/g, '')}$`) : null

  useEffect(() => {
    const getData = async () => {
      const data = await fetchTaxTypes()
      setTaxTypes(Object.values(data.data))
    }
    getData()
  }, [])

  useEffect(() => {
    if (
      (taxDetails?.type !== '' && taxDetails?.value === '') ||
      (taxDetails?.type === '' && taxDetails?.value !== '')
    ) {
      setInvalidTaxDetails && setInvalidTaxDetails(true)
    } else if (taxValueRegex && !taxValueRegex.test(taxDetails?.value || '')) {
      setInvalidTaxDetails && setInvalidTaxDetails(true)
    } else {
      setInvalidTaxDetails && setInvalidTaxDetails(false)
    }
  }, [taxDetails])

  useEffect(() => {
    if (
      taxTypes.length > 0 &&
      customerProfile?.provider?.tax_ids?.[0]?.type !== '' &&
      customerProfile?.provider?.tax_ids?.[0]?.value !== ''
    ) {
      const selectedTaxType = taxTypes.find(
        (taxType) => taxType.country === customerProfile?.provider?.tax_ids?.[0]?.country,
      )
      setTaxValuePlaceholder(selectedTaxType?.placeholder || '')
      setTaxValueValidation(selectedTaxType?.format || '')
      setTaxCountryType(selectedTaxType?.type || '')
    }
  }, [taxTypes, customerProfile?.provider?.tax_ids?.[0]?.type, customerProfile?.provider?.tax_ids?.[0]?.value])

  if (isLoading) {
    return null
  }

  return (
    <Box sx={{ marginBottom: 2.5 }}>
      <Typography variant="caption" color="grey.700" mb={0.5}>
        Billed to
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ width: '50%' }}>
          <Autocomplete
            options={taxTypes || []}
            autoComplete
            autoHighlight
            value={taxTypes.find((taxType) => taxType.type === taxCountryType) || null}
            onChange={(event, newValue) => {
              if (newValue) {
                handleTaxDetailsChange && handleTaxDetailsChange('type', newValue.stripe_type)
                setTaxCountryType(newValue.type)
                setTaxValuePlaceholder(newValue.placeholder)
                setTaxValueValidation(newValue.format)
              } else {
                handleTaxDetailsChange && handleTaxDetailsChange('type', '')
                setTaxCountryType('')
                setTaxValuePlaceholder('')
                setTaxValueValidation('')
              }
            }}
            getOptionLabel={(option) => `${option.type.replace(/_/g, ' ').toUpperCase()}` || ''}
            renderOption={(props, option) => {
              const isSelected: boolean = taxDetails?.type === option.type
              const countryCode = option.country.toUpperCase()

              return (
                <ListItem
                  {...props}
                  sx={{
                    paddingY: 1.25,
                    paddingX: 1.75,
                  }}
                >
                  <svg width={FLAG_SIZES.width} height={FLAG_SIZES.height} style={{ flexShrink: 0 }}>
                    <use href={`${sprite}#${countryCode}`} />
                  </svg>

                  <Box sx={{ marginX: 1 }}>
                    <Typography variant="body1" color="grey.900">
                      {option.type.replace(/_/g, ' ').toUpperCase()}
                    </Typography>

                    <Typography variant="body1" color="grey.500">
                      {option.description}
                    </Typography>
                  </Box>

                  {isSelected && (
                    <CheckIcon sx={{ width: '20px', height: '20px', marginLeft: 'auto', fill: '#799656' }} />
                  )}
                </ListItem>
              )
            }}
            filterOptions={(options, { inputValue }) => {
              const inputValueLower = inputValue.toLowerCase()

              return options.filter((option) => {
                const taxType = option.type.replace(/_/g, ' ').toLowerCase()
                const country = (option.country_label || option.country).toLowerCase()
                return taxType.includes(inputValueLower) || country.includes(inputValueLower)
              })
            }}
            renderInput={(params) => (
              <CustomInput
                {...params}
                placeholder="Tax ID type"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'off', // disable autocomplete and autofill
                }}
              />
            )}
            sx={AutoCompleteStyle}
            componentsProps={{
              popper: {
                sx: {
                  '& .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-disabled="true"]': {
                    opacity: '1',
                  },
                },
              },
            }}
          />
        </Box>

        <Box sx={{ width: '50%' }}>
          <InputField
            mb="0px"
            variant="outlined"
            placeholder={taxValuePlaceholder === '' ? 'Tax ID Number' : taxValuePlaceholder}
            name="taxIdNumber"
            value={taxDetails?.value || ''}
            onChange={(event) => {
              if (handleTaxDetailsChange) {
                handleTaxDetailsChange('value', event.target.value)
              }
            }}
            validations={
              taxDetails?.type !== '' || (taxDetails?.type === '' && taxDetails?.value !== '')
                ? [
                    {
                      type: ValidationType.matches,
                      params: [taxValueRegex, 'ID number should match the pattern'],
                    },
                  ]
                : []
            }
          />
        </Box>
      </Box>
    </Box>
  )
}

export default TaxForm
